// What a URL is allowed to look like by the time it reaches umami.
//
// Two separate reasons, and both matter:
//
//   CARDINALITY. /app/ro/1000123 and /app/ro/1000124 are the same screen. Left
//   alone, every repair order anyone ever opens becomes its own row in the
//   pages report and the report stops meaning anything.
//
//   PRIVACY. This app puts real values in URLs. ?redirect_url= carries
//   wherever someone was headed, and repair-order ids are in the path.
//   Neither may leave the browser.
//
//   It used to carry worse: /sign-in?user_hint=<email> was how the signup
//   wizard bounced a taken address to the sign-in screen. That parameter is
//   GONE - the two screens are in one module now (app/auth), so the hand-off
//   is router state and the address never reaches the URL bar, history, or
//   any Referer. The query-stripping below stays anyway: it costs nothing,
//   it is the reason nothing leaked while that parameter existed, and the
//   next value someone puts in a URL will not come with a note.
//
// The tracker is also configured with data-exclude-search/data-exclude-hash
// (see services/analytics.js's buildLoader), so the query string is stripped
// before this ever runs. It is stripped again here anyway: that config lives
// in a generated file served by a different process, and an email address
// escaping because two places disagreed is not a recoverable mistake.

// Ordered, and matched in order. Each entry is [test, replacement].
const PATTERNS = [
  // The sample scan is a fixed pseudo-id, not a real order - it is the whole
  // point of the "did they look at the sample" question, so it keeps its
  // identity rather than collapsing into :id.
  [/^\/app\/uploading\/sample\/?$/, '/app/uploading/sample'],
  [/^\/app\/ro\/sample\/?$/, '/app/ro/sample'],

  [/^\/app\/uploading\/[^/]+\/?$/, '/app/uploading/:id'],
  [/^\/app\/ro\/[^/]+\/?$/, '/app/ro/:id'],
  [/^\/tour\/uploading\/[^/]+\/?$/, '/tour/uploading/:id'],
  [/^\/tour\/ro\/[^/]+\/?$/, '/tour/ro/:id'],

  // Legacy pre-CMS URLs, now 301'd by services/web - worth being able to see
  // that they are still being hit, without one row per order.
  [/^\/uploading\/[^/]+\/?$/, '/uploading/:id'],
  [/^\/ro\/[^/]+\/?$/, '/ro/:id'],

  // Splat routes: /sign-in/anything and /sign-up/anything are one screen
  // each (app/auth).
  [/^\/sign-in(\/.*)?$/, '/sign-in'],
  [/^\/sign-up(\/.*)?$/, '/sign-up']
];

// Takes a pathname or a full URL, returns a pathname only - no origin, no
// query, no hash.
export function normalizePath(input) {
  let pathname = input;
  if (typeof input === 'string' && input.includes('://')) {
    try {
      pathname = new URL(input).pathname;
    } catch {
      // Unparseable: send nothing rather than send the raw string, which is
      // the one thing that could still be carrying a query.
      return '/';
    }
  }
  if (typeof pathname !== 'string' || !pathname) return '/';

  // Belt and braces - see the header comment.
  pathname = pathname.split('?')[0].split('#')[0];

  for (const [pattern, replacement] of PATTERNS) {
    if (pattern.test(pathname)) return replacement;
  }
  return pathname;
}
